import { useCallback } from 'react'
import { sleep } from '../utils'
import ElectronIPC, { FfmpegExecResult } from './ElectronIPC'
import { env } from './EnvironmentVariables'
import { ScreenRecordingInfo } from '../AppContext'

const AUDIO_ENGINE_MASTER_CAPTURE_BUFFER_S = 1

export function useScreenRecordingConverter({ pathDelimiter }: { pathDelimiter: string }) {
    const convertScreenRecording = useCallback(
        async (
            info: ScreenRecordingInfo,
            onSuccess: (fileInfo: ScreenRecordingInfo) => void,
            onError: (code: number, message?: string) => void
        ) => {
            const outputExtension = 'mp4'
            const dest = info.outputFileDirectory + pathDelimiter + info.outputFilenameBase + '.' + outputExtension

            let ffmpegArgs = [
                '-y', // overwrite
                '-i',
                info.outputFileFullPath, // the main webm file

                '-c:v',
                'copy', // the source video codec should be libx264
                '-c:a',
                'aac', // convert opus -> aac
                '-q:a',
                '0', // audio track quality loss
            ]

            if (info.proAudioFileFullPath) {
                const proAudioFileStats = await ElectronIPC.getFileStats(info.proAudioFileFullPath)
                if (proAudioFileStats) {
                    const s = AUDIO_ENGINE_MASTER_CAPTURE_BUFFER_S
                    ffmpegArgs = [
                        '-y', // overwrite
                        '-i',
                        info.outputFileFullPath, // the main webm file

                        '-i',
                        info.proAudioFileFullPath, // the audio engine audio file

                        '-filter_complex',
                        `[1:a]atrim=start=${s}[a1]; [0:a][a1]amix=inputs=2` /* :duration=first */,

                        '-c:v',
                        'copy',
                        '-c:a',
                        'aac',
                        '-q:a',
                        '0', // audio track quality loss

                        dest,
                    ]
                }
            }

            console.log(`convertWebm: calling ffmpeg with args=` + JSON.stringify(ffmpegArgs))

            const result: FfmpegExecResult = await ElectronIPC.ffmpegExec(ffmpegArgs)

            if (env.TestWebmConverterConversionDelay) {
                await sleep(env.TestWebmConverterConversionDelay)
            }

            const code = result.code
            const message = result.message || ''

            if (code != 0) {
                // error
                console.log(
                    `convertWebm: conversion ended with non-success error code ${code}; ` +
                        `error message: "${message}".`
                )

                onError(code, message)
            } else if (code == 0) {
                // success
                console.log(`convertWebm: conversion succeeded. Calling deleteFile on "${info.outputFileFullPath}"`)

                onSuccess({
                    ...info,
                    outputFilename: info.outputFilenameBase + '.' + outputExtension,
                    outputFileFullPath: dest,
                })

                setTimeout(() => {
                    ElectronIPC.deleteFile(info.outputFileFullPath)
                    if (info.proAudioFileFullPath) {
                        ElectronIPC.deleteFile(info.proAudioFileFullPath)
                    }
                }, 1000)
            }
        },
        [pathDelimiter]
    )

    return {
        convertScreenRecording,
    }
}
